import React, { useState } from 'react';
import { Briefcase, CheckCircle2, User, Phone, Mail, Link as LinkIcon, FileText, Send, Sparkles, AlertCircle, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const CareersPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    role: 'Full Stack Web Developer (React / Next.js)',
    experience: '2-5 Years (Mid-Level)',
    portfolio: '',
    coverNote: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitApplication = (e) => {
    e.preventDefault();

    if (!formData.name || !formData.phone || !formData.email) {
      toast.error('Please fill in your name, phone number, and email address.');
      return;
    }

    const applicationText = `Hello HOASP Talent Acquisition Team,%0A%0A*New Job Application / Talent Pool Submission*%0A- *Applicant Name:* ${formData.name}%0A- *Mobile:* ${formData.phone}%0A- *Email:* ${formData.email}%0A- *Target Role:* ${formData.role}%0A- *Experience Level:* ${formData.experience}%0A- *Portfolio/LinkedIn:* ${formData.portfolio || 'N/A'}%0A- *Notes/Summary:* ${formData.coverNote || 'Looking forward to career opportunities at HOASP International.'}`;

    const whatsappUrl = `https://wa.me/918796118101?text=${applicationText}`;
    const mailtoUrl = `mailto:info@hoasp.com?subject=Talent Pool Application: ${encodeURIComponent(formData.role)} - ${encodeURIComponent(formData.name)}&body=${decodeURIComponent(applicationText).replace(/%0A/g, '%0D%0A')}`;

    window.open(whatsappUrl, '_blank');

    setTimeout(() => {
      window.location.href = mailtoUrl;
    }, 500);

    setIsSubmitted(true);
    toast.success('Your application has been registered in our Talent Pool!');
  };

  return (
    <div className="pt-24 bg-gray-950 min-h-screen text-left text-gray-100 font-['Roboto',sans-serif]">
      {/* Hero Header */}
      <section className="py-20 bg-gradient-to-b from-gray-950 via-slate-900 to-gray-950 border-b border-gray-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b30_1px,transparent_1px),linear-gradient(to_bottom,#1e293b30_1px,transparent_1px)] bg-[size:3rem_3rem] pointer-events-none" />

        <div className="container mx-auto px-6 max-w-4xl text-center relative z-10">
          <div className="inline-flex items-center space-x-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-1.5 mb-4">
            <Briefcase className="w-4 h-4 text-cyan-400" />
            <span className="text-cyan-400 text-sm font-semibold uppercase tracking-wider">
              Careers & Engineering Culture
            </span>
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
            Build the Future of Enterprise Tech <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-orange-400 bg-clip-text text-transparent">With Us</span>
          </h1>
          <p className="text-gray-300 text-base md:text-lg leading-relaxed max-w-3xl mx-auto">
            We are always on the lookout for visionary software engineers, cloud architects, AI developers, and UI/UX designers to join our talent database for global enterprise projects.
          </p>
        </div>
      </section>

      {/* Perks & Culture */}
      <section className="py-16 bg-slate-50 border-b border-gray-200">
        <div className="container mx-auto px-6">
          <h2 className="text-2xl font-extrabold text-gray-900 mb-8 text-center">Why Work With HOASP International?</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-md hover:shadow-xl hover:border-cyan-500/50 transition-all">
              <CheckCircle2 className="w-6 h-6 text-cyan-600 mb-3" />
              <h3 className="text-base font-bold text-gray-900 mb-1">Cutting-Edge Tech</h3>
              <p className="text-xs text-gray-600 leading-relaxed">Work with React 19, Next.js 15, AI LLMs, PyTorch, GraphQL, and cloud microservices.</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-md hover:shadow-xl hover:border-orange-500/50 transition-all">
              <CheckCircle2 className="w-6 h-6 text-orange-500 mb-3" />
              <h3 className="text-base font-bold text-gray-900 mb-1">Flexible Work Options</h3>
              <p className="text-xs text-gray-600 leading-relaxed">Remote and hybrid options with flexible working hours for optimal work-life balance.</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-md hover:shadow-xl hover:border-amber-500/50 transition-all">
              <CheckCircle2 className="w-6 h-6 text-amber-500 mb-3" />
              <h3 className="text-base font-bold text-gray-900 mb-1">Competitive Compensation</h3>
              <p className="text-xs text-gray-600 leading-relaxed">Top-of-market salary packages, performance incentives, and comprehensive health insurance.</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-md hover:shadow-xl hover:border-blue-500/50 transition-all">
              <CheckCircle2 className="w-6 h-6 text-blue-600 mb-3" />
              <h3 className="text-base font-bold text-gray-900 mb-1">Accelerated Growth</h3>
              <p className="text-xs text-gray-600 leading-relaxed">Dedicated learning budgets, global team exposure, and fast-track career promotions.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Current Openings Status Section */}
      <section className="py-20 bg-gray-950 border-b border-gray-800">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
            <div>
              <h2 className="text-3xl font-extrabold text-white">Current Openings</h2>
              <p className="text-sm text-gray-400">Live job listings and recruitment status</p>
            </div>
            <span className="text-xs font-mono text-amber-400 bg-amber-500/10 px-4 py-1.5 rounded-full border border-amber-500/20 flex items-center gap-2">
              <Clock className="w-3.5 h-3.5" /> 0 Immediate Openings Active
            </span>
          </div>

          {/* No Immediate Openings Card */}
          <div className="bg-gray-900/90 border border-gray-800 rounded-3xl p-8 md:p-10 text-center mb-16 shadow-xl relative overflow-hidden">
            <div className="w-14 h-14 bg-amber-500/10 border border-amber-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4 text-amber-400">
              <AlertCircle className="w-7 h-7" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">No Immediate Openings Right Now</h3>
            <p className="text-gray-300 text-sm md:text-base max-w-2xl mx-auto leading-relaxed mb-6">
              Currently, we do not have active job vacancies open for immediate hire. However, we continuously expand our engineering teams for new client projects.
            </p>
            <div className="inline-flex items-center space-x-2 text-cyan-400 text-xs font-bold uppercase tracking-wider bg-cyan-500/10 px-4 py-1.5 rounded-full border border-cyan-500/20">
              <Sparkles className="w-3.5 h-3.5" /> Register Below to Join Our Priority Talent Pool
            </div>
          </div>

          {/* Talent Pool Application Form */}
          <div id="apply" className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12 shadow-2xl text-left text-gray-900">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-8">
                <span className="text-orange-600 text-xs font-bold uppercase tracking-wider bg-orange-50 border border-orange-200 px-3 py-1 rounded-full">
                  Talent Network Registration
                </span>
                <h3 className="text-2xl md:text-3xl font-extrabold text-gray-900 mt-2 mb-2">
                  Apply for Future Job Roles
                </h3>
                <p className="text-gray-600 text-sm">
                  Submit your profile to our talent database. Our HR team will reach out to you first when a matching position opens.
                </p>
              </div>

              {isSubmitted ? (
                <div className="bg-slate-50 border border-emerald-300 rounded-2xl p-8 text-center shadow-sm">
                  <div className="w-16 h-16 bg-emerald-100 border border-emerald-300 rounded-full flex items-center justify-center mx-auto mb-4 text-emerald-600">
                    <CheckCircle2 className="w-10 h-10" />
                  </div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-2">Application Transmitted!</h4>
                  <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                    Thank you <strong className="text-gray-900">{formData.name}</strong>. Your profile for <strong className="text-cyan-600">{formData.role}</strong> has been transmitted to HOASP International HR via WhatsApp and Email.
                  </p>
                  <Button
                    onClick={() => setIsSubmitted(false)}
                    className="bg-cyan-600 hover:bg-cyan-700 text-white font-bold px-6 py-3 rounded-xl text-sm"
                  >
                    Submit Another Profile
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmitApplication} className="space-y-5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Full Name *</label>
                    <div className="relative">
                      <User className="w-4 h-4 text-gray-400 absolute left-3.5 top-3.5" />
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="e.g. Vikram Verma"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full bg-slate-50 border border-gray-300 rounded-xl pl-10 pr-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-cyan-500 focus:bg-white transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Mobile Phone / WhatsApp *</label>
                      <div className="relative">
                        <Phone className="w-4 h-4 text-gray-400 absolute left-3.5 top-3.5" />
                        <input
                          type="tel"
                          name="phone"
                          required
                          placeholder="+91 98765 43210"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full bg-slate-50 border border-gray-300 rounded-xl pl-10 pr-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-cyan-500 focus:bg-white transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Email Address *</label>
                      <div className="relative">
                        <Mail className="w-4 h-4 text-gray-400 absolute left-3.5 top-3.5" />
                        <input
                          type="email"
                          name="email"
                          required
                          placeholder="name@example.com"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full bg-slate-50 border border-gray-300 rounded-xl pl-10 pr-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-cyan-500 focus:bg-white transition-colors"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Target Role / Domain</label>
                      <select
                        name="role"
                        value={formData.role}
                        onChange={handleInputChange}
                        className="w-full bg-slate-50 border border-gray-300 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:border-cyan-500 focus:bg-white transition-colors"
                      >
                        <option value="Full Stack Web Developer (React / Next.js)">Full Stack Web Developer (React / Next.js)</option>
                        <option value="Mobile App Engineer (React Native / Flutter)">Mobile App Engineer (React Native / Flutter)</option>
                        <option value="AI / LLM Software Engineer">AI / LLM Software Engineer</option>
                        <option value="Cloud & DevOps Engineer">Cloud & DevOps Engineer</option>
                        <option value="UI/UX Product Designer">UI/UX Product Designer</option>
                        <option value="QA / Automation Tester">QA / Automation Tester</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Experience Level</label>
                      <select
                        name="experience"
                        value={formData.experience}
                        onChange={handleInputChange}
                        className="w-full bg-slate-50 border border-gray-300 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:border-cyan-500 focus:bg-white transition-colors"
                      >
                        <option value="0-2 Years (Junior)">0-2 Years (Junior)</option>
                        <option value="2-5 Years (Mid-Level)">2-5 Years (Mid-Level)</option>
                        <option value="5+ Years (Senior / Lead)">5+ Years (Senior / Lead)</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Portfolio / GitHub / LinkedIn URL</label>
                    <div className="relative">
                      <LinkIcon className="w-4 h-4 text-gray-400 absolute left-3.5 top-3.5" />
                      <input
                        type="url"
                        name="portfolio"
                        placeholder="https://linkedin.com/in/username or https://github.com/..."
                        value={formData.portfolio}
                        onChange={handleInputChange}
                        className="w-full bg-slate-50 border border-gray-300 rounded-xl pl-10 pr-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-cyan-500 focus:bg-white transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Brief Cover Note / Key Skills</label>
                    <textarea
                      name="coverNote"
                      rows="3"
                      placeholder="Mention your core tech stack, current notice period, or career aspirations..."
                      value={formData.coverNote}
                      onChange={handleInputChange}
                      className="w-full bg-slate-50 border border-gray-300 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-cyan-500 focus:bg-white transition-colors"
                    ></textarea>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-orange-500 via-amber-500 to-cyan-600 hover:from-orange-600 hover:to-cyan-700 text-white font-bold py-4 rounded-xl shadow-lg shadow-orange-500/20 flex items-center justify-center gap-2"
                  >
                    <Send className="w-4 h-4" /> Submit Application to HR (+91 8796 118 101)
                  </Button>
                </form>
              )}
            </div>
          </div>

        </div>
      </section>
    </div>
  );
};

export default CareersPage;
